import { useEffect, useRef } from 'react'
import { notifications } from '@mantine/notifications'
import { useApiClient, useSession } from '../../api'
import { getStoredReferralCode, clearStoredReferralCode } from '../../referral'

// One-shot referral attachment (contract §4.22). main.jsx captures ?ref=<code>
// into localStorage before render; once the signed-in backend session is up
// (useSession().me resolved), this posts the stored code exactly once.
//
// Outcome handling: definitive server answers clear the stored code —
// success ("Referral applied" toast), 404 not_found (unknown code) and
// 400 invalid (own code / referrer already set / account older than 7 days)
// all mean a retry can never succeed, so the failure is silent and final.
// Transient failures keep the code for a later session: network errors
// (ApiError code 'network', status 0), 5xx (backend restarting behind
// nginx answers 502), and 429 (rate limited).
export default function ReferralAttach() {
  const api = useApiClient()
  const { me } = useSession()
  const attemptedRef = useRef(false)

  useEffect(() => {
    if (!me || attemptedRef.current) return
    const code = getStoredReferralCode()
    if (!code) return
    attemptedRef.current = true
    ;(async () => {
      try {
        await api.post('/api/v1/me/referral', { code })
        clearStoredReferralCode()
        notifications.show({ color: 'green', message: 'Referral applied' })
      } catch (err) {
        // Transient: keep the code for a later session.
        if (err?.code === 'network' || err?.status >= 500 || err?.status === 429) return
        clearStoredReferralCode() // 400 / 404: a retry can never succeed
      }
    })()
  }, [me, api])

  return null
}
